import React, { useState, useEffect, useMemo, useRef } from 'react';
import { UserCircleCheck } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  trackAnalysisFinish,
  trackAnalysisClickOnPerson,
} from '../../../analytics';

import { SellingPoint } from '../../../components/custom';
import { Button, Alert } from '../../../components/general';
import {
  PageWrapper,
  PageHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL, UG_ID } from '../../../constants';

import useAuthUserStore from '../../../hooks/useAuthUserStore';
import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { getCreationResults } from '../../../services/api/creations';
import { getReviewData } from '../../../services/videoBuilderService';

import { useProjectStore } from '../../../stores/project';
import { useVideoBuilderStore } from '../../../stores/videoBuilder';

import {
  Person,
  HlReel,
  Shot,
  AnalysisClips,
  ClipSelection,
  PageError,
  ModalError,
} from '../../../types';
import { isEmpty, getUrl } from '../../../utils';

import DoneBtn from '../components/doneBtn';
import {
  getClipSelections,
  getPersonWildcardsData,
} from '../components/getAnalysisData';
import { getReviewText } from '../components/getText';
import ClipsBlocks from './components/clipsBlocks';
import GenerationBlock from './components/generationBlock';
import HlReelPreviewContainer from './components/hlReelPreviewContainer';
import PeopleCompleteBlock from './components/peopleCompleteBlock';
import PeopleOtherBlock from './components/peopleOtherBlock';
import PersonClipsModal from './components/personClipsModal';

const Review = () => {
  const { eventId = '' } = useParams();

  const { adminMode } = useAuthUserStore();
  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();

  const { setClipSelections: setClipSelectionsStore } = useVideoBuilderStore();

  const [isAnalysing, setIsAnalysing] = useState(false);
  const [clipSelections, setClipSelections] = useState<ClipSelection[]>([]);

  // people
  const [people, setPeople] = useState<Person[]>([]);
  const [generatedPeopleIds, setGeneratedPeopleIds] = useState<string[]>([]);
  const [openedPersonId, setOpenedPersonId] = useState<string | null>(null);

  // highlight reel
  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  // clips
  const [clips, setClips] = useState<AnalysisClips | null>(null);

  // selection
  const hasSelectedPeople = useRef(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);

  const navigate = useNavigate();

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchAnalysisData();
      await fetchGenerationData();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      return;
    }

    if (currentProject) onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, projectError]);

  // on mount – show UG if required
  useEffect(() => {
    const isShow = sessionStorage.getItem('showSampleUG') === 'true';
    if (isShow) window.userGuiding.previewGuide(UG_ID.SAMPLE_P2);
  }, []);

  // if still analysing – redirect to Analysis page; else – add GA event
  useEffect(() => {
    if (isLoading) return;

    if (isAnalysing) navigate(getUrl(URL.VB_ANALYSIS, eventId));
    else {
      const projectsUpd = JSON.parse(
        localStorage.getItem('analysedProjects') ?? '[]',
      );

      // check if eventId is not in localStorage analysedProjects – to avoid duplicate events
      if (!projectsUpd.includes(eventId)) {
        trackAnalysisFinish(isSample);

        projectsUpd.push(eventId);
        localStorage.setItem('analysedProjects', JSON.stringify(projectsUpd));
      }
    }
  }, [eventId, isAnalysing, isSample, isLoading, navigate]);

  // clip selections – find the matching clips for each person's wildcard spot
  useEffect(() => {
    if (isLoading) return;

    const clipSelectionsTemp = getClipSelections({ people, requiredShots });

    setClipSelections(clipSelectionsTemp);
    setClipSelectionsStore(clipSelectionsTemp);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, people, requiredShots]);

  // clip groups – get complete people, incomplete people from most filled to least, and generated people
  const { completePeople, incompletePeople, generatedPeople } = useMemo(() => {
    let completePeople: Person[] = [];
    let incompletePeople: Person[] = [];
    let generatedPeople: Person[] = [];

    if (isEmpty(people) || isEmpty(clipSelections))
      return { completePeople, incompletePeople, generatedPeople };

    people.forEach((person) => {
      const { missingClips, isEnoughClips } = getPersonWildcardsData(
        person.face.elementId,
        requiredShots,
        clipSelections,
      );

      person.face.missingClips = missingClips;

      if (generatedPeopleIds.includes(person.face.elementId))
        generatedPeople.push(person);
      else if (isEnoughClips) completePeople.push(person);
      else incompletePeople.push(person);
    });

    incompletePeople.sort(
      (one, other) =>
        (one.face.missingClips?.length ?? 0) -
        (other.face.missingClips?.length ?? 0),
    );

    return { completePeople, incompletePeople, generatedPeople };
  }, [people, clipSelections, generatedPeopleIds, requiredShots]);

  // set selected people IDs (by default has all complete people)
  useEffect(() => {
    // this action is only performed once
    if (hasSelectedPeople.current || isEmpty(completePeople)) return;

    const completePeopleIds: string[] = [];
    completePeople.map((p) => completePeopleIds.push(p.face.elementId));
    setSelectedPeopleIds(completePeopleIds);
    hasSelectedPeople.current = true;
  }, [completePeople]);

  const fetchAnalysisData = async () => {
    try {
      const id = isSample ? sampleProjectData.event.eventId : eventId;

      const { people, hlReel, requiredShots, clips, isAnalysed } =
        await getReviewData({ eventId: id, isSample });

      setIsAnalysing(!isAnalysed);

      if (isAnalysed) {
        setPeople(people);
        setHlReel(hlReel);
        setRequiredShots(requiredShots);
        setClips(clips);
      }
    } catch (err) {
      console.error(`Error fetching analysis data: ${err}`);
      setError({ subheading: 'Error fetching analysis data' });
    }
  };

  const fetchGenerationData = async () => {
    try {
      // sample project
      if (isSample) {
        const generatedPeopleIds = sampleProjectData.people
          .filter((p: Person) => p.isGenerated)
          .map((p: Person) => p.face.elementId);

        setGeneratedPeopleIds(generatedPeopleIds);
      }

      // usual project
      else {
        const generatedPeople = await getCreationResults(eventId);

        setGeneratedPeopleIds(generatedPeople.map((p) => p.face.elementId));
      }
    } catch (err) {
      console.error(`Error fetching generation data: ${err}`);
      setError({ subheading: 'Error fetching generation data' });
    }
  };

  const handleClickPerson = (person: Person, label: string) => {
    setOpenedPersonId(person.face.elementId);
    trackAnalysisClickOnPerson(label, isSample);
  };

  // when elementId provided – toggle the person selection; else – select or deselect ALL
  const onClickCheckbox = (elementId: string | null, isAddAll?: boolean) => {
    let tempIds: string[] = [];

    if (!!elementId)
      tempIds = selectedPeopleIds.includes(elementId)
        ? selectedPeopleIds.filter((id) => id !== elementId)
        : [...selectedPeopleIds, elementId];
    else if (isAddAll)
      completePeople.map((p) => tempIds.push(p.face.elementId));

    setSelectedPeopleIds(tempIds);
  };

  const { reviewAlert, reviewText, toGenerateText } = getReviewText({
    currentProject,
    hlReel,
    requiredShots,
    completePeople,
    incompletePeople,
    generatedPeople,
    selectedPeopleIds,
    isAnalysisPage: true,
    isSample,
  });

  const allowGenerate = !reviewAlert;

  return (
    <PageWrapper
      type="project"
      title={`Review – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <div className="flex flex-col gap-8">
        <PageHeader
          heading="Review"
          icons={
            !isSample &&
            !isEmpty(completePeople) &&
            !isSelectionMode && (
              <Button
                onClick={() => setIsSelectionMode(true)}
                variant="square"
                colour="transparent-black"
                popover={{
                  header: 'Custom Video Generation',
                  body: 'Select specific people to generate videos for',
                }}
              >
                <UserCircleCheck size="1.25rem" />
              </Button>
            )
          }
        />

        {reviewAlert && (
          <Alert alertData={{ variant: 'warning', text: reviewAlert }} />
        )}

        {reviewText && <SellingPoint text={reviewText} />}

        <PeopleCompleteBlock
          people={completePeople}
          handleClickPerson={handleClickPerson}
          isSelectionMode={isSelectionMode}
          selectedPeopleIds={selectedPeopleIds}
          onClickCheckbox={onClickCheckbox}
        />

        {allowGenerate && (
          <GenerationBlock
            toGenerateText={toGenerateText ?? ''}
            selectedPeopleIds={selectedPeopleIds}
          />
        )}

        {!isEmpty(completePeople) && <hr />}

        <PeopleOtherBlock
          incompletePeople={incompletePeople}
          generatedPeople={generatedPeople}
          handleClickPerson={handleClickPerson}
        />

        <ClipsBlocks clips={clips} fetchData={fetchAnalysisData} />

        <HlReelPreviewContainer hlReel={hlReel} requiredShots={requiredShots} />

        {!allowGenerate && <DoneBtn isBack />}
      </div>

      <PersonClipsModal
        openedPersonId={openedPersonId}
        setOpenedPersonId={setOpenedPersonId}
        people={completePeople
          .concat(incompletePeople)
          .concat(adminMode ? generatedPeople : [])}
        hlReel={hlReel}
        requiredShots={requiredShots}
      />

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        btn={error?.btn}
        isTryAgainBtn={!error?.btn}
      />
    </PageWrapper>
  );
};

export default Review;
